import { type ReactElement } from 'react';
import {
  Card,
  Text,
  Space,
  Button,
  Group,
  Modal,
  ThemeIcon,
  ScrollArea,
} from '@mantine/core';
import { FaInfo } from 'react-icons/fa';
import { HiOutlinePlus } from 'react-icons/hi';
import { TbTrashXFilled } from 'react-icons/tb';
import { useDisclosure } from '@mantine/hooks';
import { ClientModal } from './ClientModal';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { deleteFetcher, getFetcher, postFetcher } from '@/api/fetchers';
import { type UnwantedClients } from '@/schemas/API/GET/employee/preferences/response';
import React from 'react';

export const EmployeeClientPreferencesSection = (): ReactElement => {
  const [modalOpened, { open, close }] = useDisclosure(false);

  const { employeeId } = useParams<{ employeeId: string }>();
  const { data: unwantedClients, mutate: mutateUnwantedClients } =
    useSWR<UnwantedClients>(
      employeeId ? `/employees/${employeeId}/unwanted-clients` : null,
      getFetcher
    );

  const deleteClient = async (clientId: number) => {
    await deleteFetcher(`employees/${employeeId}/unwanted-clients/${clientId}`);
    mutateUnwantedClients(
      {
        unwantedList: unwantedClients
          ? unwantedClients.unwantedList.filter(
              client => client.id !== clientId
            )
          : [],
      },
      false
    );
  };
  const handleNewClients = async (newClients: number[]) => {
    const updatedClients: UnwantedClients = await postFetcher(
      `/employees/${employeeId}/unwanted-clients`,
      {
        entityIds: newClients,
      }
    );
    mutateUnwantedClients(updatedClients, false);
  };

  const clientRows = unwantedClients?.unwantedList.map(client => (
    <React.Fragment key={client.id}>
      <Card shadow="none" withBorder>
        <Group justify="space-between">
          <Group>
            <Text>{client.firstName}</Text>
            <Text>{client.lastName}</Text>
          </Group>
          <Button
            data-testid={client.id}
            variant="subtle"
            onClick={() => {
              deleteClient(client.id);
            }}
          >
            <TbTrashXFilled color="black" size="1.9em" />
          </Button>
        </Group>
      </Card>
      <Space h="lg" />
    </React.Fragment>
  ));

  return (
    <Card shadow="sm" radius="md" withBorder>
      <Group justify="space-between">
        <Text fw={700} size="lg">
          Nežádoucí klienti
        </Text>
        <Button
          data-testid="add"
          variant="subtle"
          onClick={() => {
            open();
          }}
        >
          <HiOutlinePlus color="black" size="1.9em" />
        </Button>
      </Group>
      <Space h="lg" />
      {unwantedClients && unwantedClients.unwantedList.length === 0 ? (
        <Card shadow="none" withBorder>
          <Group justify="space-between">
            <Group>
              <ThemeIcon variant="white">
                <FaInfo style={{ width: '70%', height: '70%' }} />
              </ThemeIcon>
              <p>Nejsou vybráni nežádoucí klienti</p>
            </Group>
          </Group>
        </Card>
      ) : (
        <ScrollArea>{clientRows}</ScrollArea>
      )}
      <Modal
        opened={modalOpened}
        onClose={close}
        centered
        withCloseButton={false}
        size="lg"
      >
        <ClientModal
          closeModal={(newClients: number[] | null) => {
            close();
            if (newClients !== null) {
              handleNewClients(newClients);
            }
          }}
          startIds={
            unwantedClients
              ? unwantedClients.unwantedList.map(client => client.id)
              : []
          }
        ></ClientModal>
      </Modal>
    </Card>
  );
};
